<template>
  <footer class="bg-black text-white border-t border-white/10">
    <div class="px-6 lg:px-16 xl:px-40 py-12 md:py-16">
      <div class="flex flex-col md:flex-row md:justify-between gap-12">
        <div class="flex flex-col gap-6">
          <RouterLink
            :to="{ name: 'home' }"
            class="inline-block text-[#ededed] hover:text-primary transition-colors"
            aria-label="ZNIPP"
          >
            <Logo class="h-[80px] md:h-[97px] w-auto" aria-hidden="true" />
          </RouterLink>

          <div class="flex flex-col gap-2 text-[16px]">
            <a
              v-for="link in linkedinLinks" :key="link.href"
              :href="link.href"
              target="_blank"
              rel="noopener"
              class="inline-flex items-center gap-2 text-white hover:text-primary transition-colors"
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                class="w-[18px] h-[18px] shrink-0"
                aria-hidden="true"
              >
                <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.95v5.66H9.36V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.38-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.23 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0h.01z" />
              </svg>
              <span>{{ t(link.textKey) }}</span>
            </a>
          </div>

          <address class="flex flex-col gap-1 not-italic text-[16px] text-white/80">
            <a :href="`tel:${phoneNumber}`" class="text-white hover:text-primary transition-colors mt-2">{{ t('footer.phone') }}</a>
            <a :href="`mailto:${emailAddress}`" class="text-white hover:text-primary transition-colors">{{ t('footer.email') }}</a>
            <span>{{ t('footer.address.street') }}</span>
            <span>{{ t('footer.address.city') }}</span>
            <span>{{ t('footer.address.country') }}</span>
          </address>
        </div>

        <nav class="grid grid-cols-3 gap-6 md:gap-10 md:text-right text-[16px] self-start">
          <ul
            v-for="(column, idx) in linkColumns" :key="idx"
            class="flex flex-col gap-3 list-none pl-0 m-0"
          >
            <li v-for="item in column" :key="item.name" class="m-0">
              <RouterLink :to="{ name: item.name }">{{ t(item.textKey) }}</RouterLink>
            </li>
          </ul>
        </nav>
      </div>

      <div class="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-sm">
        <p class="m-0 text-white/60">© {{ year }} ZNIPP GmbH</p>
        <div class="flex gap-4">
          <RouterLink
            v-for="language in languages"
            :key="language"
            :to="getRoute(language)"
            class="transition-colors"
            :class="language === currentLanguage ? 'text-white' : 'text-white/60'"
          >
            {{ languageName(language) }}
          </RouterLink>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import Config from '@/utils/config'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import Logo from '@/assets/images/logo.svg'

const { t, locale } = useI18n()
const route = useRoute()
const languages = Config.languages ?? []

const year = new Date().getFullYear()

const linkedinLinks = [
  { textKey: 'social.linkedinStefan', href: Config.linkedinStefan },
  { textKey: 'social.linkedinZnipp', href: Config.linkedinZnipp },
]

const linkColumns = [
  [
    { name: 'blog', textKey: 'menu.blog' },
    { name: 'about', textKey: 'menu.about' },
    { name: 'contact', textKey: 'menu.contact' },
    { name: 'support', textKey: 'menu.support' },
  ],
  [
    { name: 'services', textKey: 'menu.services' },
    { name: 'workbench', textKey: 'menu.workbench' },
    { name: 'approach', textKey: 'menu.approach' },
    { name: 'cases', textKey: 'menu.cases' },
  ],
  [
    { name: 'imprint', textKey: 'menu.imprint' },
    { name: 'privacy', textKey: 'menu.privacy' },
    { name: 'terms', textKey: 'menu.terms' },
  ],
] as const

const phoneNumber = computed(() => t('footer.phone').replace(/\s+/g, ''))
const emailAddress = computed(() => t('footer.email'))

const languageName = (language: string) => {
  const names: Record<string, string> = { de: 'Deutsch', en: 'English' }
  return names[language] ?? language
}

const currentLanguage = computed(() => (route.params.locale as string) || locale.value)

const plainRoute = computed(() => {
  return route.path.replace('/' + route.params.locale, '')
})

const getRoute = (language: string) => {
  return '/' + language + (plainRoute.value.startsWith('/') ? '' : '/') + plainRoute.value
}
</script>
